import React from 'react';
import { useTheme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import useMediaQuery from '@mui/material/useMediaQuery';
import scoreColors from 'global/styles/scoreColors';
import { CircularProgress } from '@mui/material';
import { getAccountSetting } from 'redux/actions/userLogin';
import { SETTINGS } from 'feature/admin/account-settings';
import { get } from 'global/lib/lodash';

const useMobileScreen = () => {
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.up('lg'));
  return !matches;
};

const useMediaMatch = (str = 'lg') => {
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.up(str));
  return matches;
};

const useMediaMatchDown = (str = 'lg') => {
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.down(str));
  return matches;
};

const useMobileScreenXS = () => {
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.up('md'));
  return !matches;
};

//only use this out of all the mediaquery stuff
export const useScreen = (str = 'lg') => {
  const isUp = useMediaMatch(str);
  const isDown = useMediaMatchDown(str);
  const isMobile = useMobileScreen();
  const isMobileXS = useMobileScreenXS();
  const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0 || navigator.msMaxTouchPoints > 0;

  return {
    isUp,
    isDown,
    isMobile,
    isMobileXS,
    isTouchDevice,
  };
};

export const isDark = (theme) => {
  return get(theme, 'palette.mode') === 'dark';
};

export const grey = (theme, val) => {
  return get(theme, `palette.grey.${val}`);
};

export const getColorFromValue = (value) => {
  // expects a value between 0 and 100 or -1
  const val = (() => {
    if (value < 0) {
      return '';
    } else {
      return Math.floor(value / 10);
    }
  })();
  return scoreColors[`score${val}`];
};

export const getColorFromScore = (score, minMax, isBw = false) => {
  // expects a score between 0 and 100 or -1
  const def = [0, 100];
  const ranges = getAccountSetting(SETTINGS.assessmentColorScale);
  const [min, max] = minMax || ranges || def;
  const val = (() => {
    if (score < 0) {
      return '';
    } else if (score <= min) {
      return 0;
    } else if (score >= max) {
      return 10;
    } else {
      return Math.floor(((score - min) * (100 / (max - min))) / 10);
    }
  })();
  return scoreColors[`score${isBw ? 'bw' : ''}${val}`];
};

export const classificationToColor = (str) => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  let value = (hash >> 8) & 0xff;
  if (value < 120) {
    value = value + 120;
  }
  if (value > 200) {
    value = value - 55;
  }
  let colour = '#';
  for (let x = 0; x < 3; x++) {
    colour += ('FF' + value.toString(16)).substr(-2);
  }
  return colour;
};

export const screenDims = () => {
  return {
    ...window.screen,
  };
};

export const imageStyle = (dim = '100%') => {
  return { maxWidth: dim, maxHeight: dim, height: 'auto', width: 'auto' };
};

export const Loading = <CircularProgress size={12} />;

export const Loader = (props) => <CircularProgress {...props} />;

export const CenterLoader = (props) => {
  const classes = Styles();

  return (
    <div {...props} className={classes.centerColumn}>
      <Loader {...props} />
    </div>
  );
};

const Styles = makeStyles((theme) => ({
  centerColumn: {
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    display: 'flex',
    height: '100%',
    flex: '1 1 auto',
  },
  row: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  imageWrap: {
    pageBreakInside: 'avoid !important',
    '& > div': {
      backgroundColor: `${theme.colors.transparent} !important`,
      display: 'flex',
      height: '100%',
      paddingTop: '0 !important',
    },
    '& > div img': {
      width: 'auto !important',
      height: 'auto !important',
      maxWidth: '100%',
      maxHeight: '100%',
      top: 'auto !important',
      left: 'auto !important',
    },
  },
  clickableImage: {
    '&.IMAGE': {
      position: 'relative',
      cursor: 'pointer',

      '&:hover': {
        opacity: 0.9,
      },
    },
  },
  checkIcon: {
    position: 'absolute',
    top: 0,
    left: 0,
  },
  checkIconSelected: {
    color: theme.colors.black,
    '& svg': {
      backgroundColor: theme.colors.white,
    },
  },
  formContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    margin: '0 auto',

    [theme.breakpoints.between('xs', 'lg')]: {
      width: 'auto',
    },

    '& .MuiInputBase-root': {
      width: '100%',
    },
    '& .MuiFormGroup-root': {
      backgroundColor: theme.palette.grey['200'],
      width: '85%',
      marginTop: theme.spacing(3),
      padding: `0 ${theme.spacing(1)}`,
    },
  },
  printOnly: {
    display: 'none',

    '@media print': {
      display: 'block',
    },
  },
  processingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',

    '& .processText': {
      fontSize: 12,
      marginTop: theme.spacing(1),
    },
  },
  imageListItem: {
    display: 'flex',
    flexDirection: 'column',
    pageBreakInside: 'avoid',
  },
  imgCaption: {
    fontSize: 10,
    color: theme.palette.grey['500'],
    padding: 1,
  },
  select: {
    borderRadius: 4,
    marginLeft: theme.spacing(1),
    '& .MuiFilledInput-input': {
      padding: '5px 12px 6px',
      paddingRight: 32,
    },
  },
  printLogoContainer: {
    '& .IMAGE .mui-image-wrapper': {
      justifyContent: 'flex-start !important',
    },
  },

  imageCarousel: {
    '& .imageItem': {
      height: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
  },
  linkSelect: {
    color: theme.palette.secondary.main,
    textTransform: 'none',
  },
  mlButton: {
    background: 'linear-gradient(45deg, #8A2BE2 30%, #DA70D6 90%)',
    color: 'white',
    '&:hover': {
      background: 'linear-gradient(45deg, #6A0DAD 30%, #C71585 90%)',
    },
  },
}));

export default Styles;
